import { css, html, LitElement, unsafeCSS } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { classMap } from 'lit/directives/class-map.js';
import { getMessage } from '@/localization.js';
import styles from './style.scss?inline';
import { ANIMATION, DEFAULTS, MEASURE } from './constants.js';
import messages from './localization.json';

export default class SotbitCollapsibleText extends LitElement {
    static properties = {
        lines: { type: Number },
        _expanded: { type: Boolean, state: true },
        _needsToggle: { type: Boolean, state: true },
        _lineHeightPx: { type: Number, state: true },
        _collapsedHeightPx: { type: Number, state: true },
    };

    static styles = css`
        ${unsafeCSS(styles)}
    `;

    constructor() {
        super();
        this.lines = DEFAULTS.LINES;
        this._expanded = false;
        this._needsToggle = false;
        this._lineHeightPx = 0;
        this._collapsedHeightPx = 0;
        this._outerRef = createRef();
        this._innerRef = createRef();
        this._anim = null;
        this._lineClampRafId = 0;
        this._resizeObserver = null;
        this._animating = false;
    }

    connectedCallback() {
        super.connectedCallback();
        this._resizeObserver = new ResizeObserver(() => {
            this._scheduleMeasure();
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._cancelAnimation();
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
    }

    firstUpdated() {
        this._observeInner();
        this._scheduleMeasure();
    }

    updated(changed) {
        if (changed.has('lines')) {
            this._scheduleMeasure();
        }
        if (!this._animating) {
            this._syncFromState();
        }
    }

    _observeInner() {
        const inner = this._innerRef.value;
        if (inner && this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver.observe(inner);
        }
    }

    _scheduleMeasure() {
        requestAnimationFrame(() => this._measure());
    }

    _cancelAnimation() {
        if (this._lineClampRafId) {
            cancelAnimationFrame(this._lineClampRafId);
            this._lineClampRafId = 0;
        }
        if (this._anim) {
            this._anim.cancel();
            this._anim = null;
        }
    }

    _applyLineClamp(inner, lineCount) {
        const n = Math.max(1, Math.round(lineCount));
        inner.style.display = '-webkit-box';
        inner.style.webkitBoxOrient = 'vertical';
        inner.style.overflow = 'hidden';
        inner.style.webkitLineClamp = String(n);
        inner.style.textOverflow = 'ellipsis';
    }

    _clearLineClamp(inner) {
        inner.style.display = '';
        inner.style.webkitBoxOrient = '';
        inner.style.overflow = '';
        inner.style.webkitLineClamp = '';
        inner.style.textOverflow = '';
    }

    _maxContentLines(inner) {
        if (!this._lineHeightPx) {
            return this.lines;
        }
        return Math.max(this.lines, Math.ceil(inner.scrollHeight / this._lineHeightPx));
    }
    _readLineHeightPx(element) {
        const cs = getComputedStyle(element);
        const lh = cs.lineHeight;
        if (lh === 'normal') {
            const probe = document.createElement('div');
            probe.style.cssText = [
                'position:absolute',
                'visibility:hidden',
                'pointer-events:none',
                'white-space:nowrap',
                `font:${cs.font}`,
                'line-height:normal',
            ].join(';');
            probe.textContent = '\u00a0';
            element.appendChild(probe);
            const h = probe.offsetHeight;
            probe.remove();
            return h || parseFloat(cs.fontSize) * MEASURE.LINE_HEIGHT_TO_FONT_RATIO;
        }
        const parsed = parseFloat(lh);
        return Number.isFinite(parsed) ? parsed : parseFloat(cs.fontSize) * MEASURE.LINE_HEIGHT_TO_FONT_RATIO;
    }

    _measure() {
        const inner = this._innerRef.value;
        if (!inner) {
            return;
        }

        this._lineHeightPx = this._readLineHeightPx(inner);

        this._clearLineClamp(inner);
        const full = inner.scrollHeight;

        this._applyLineClamp(inner, this.lines);
        void inner.offsetHeight;
        this._collapsedHeightPx = Math.ceil(inner.getBoundingClientRect().height);

        const needsToggle =
            this._collapsedHeightPx > 0 && full > this._collapsedHeightPx + MEASURE.SCROLL_OVERFLOW_EPSILON_PX;
        this._needsToggle = needsToggle;

        if (this._expanded || !needsToggle) {
            this._clearLineClamp(inner);
        }

        this.requestUpdate();
    }

    _syncFromState() {
        if (this._animating) {
            return;
        }
        const outer = this._outerRef.value;
        const inner = this._innerRef.value;
        if (!outer || !inner) {
            return;
        }

        outer.style.height = '';
        outer.style.overflow = '';

        if (!this._needsToggle) {
            this._clearLineClamp(inner);
            return;
        }

        if (this._expanded) {
            this._clearLineClamp(inner);
        } else {
            this._applyLineClamp(inner, this.lines);
        }
    }

    _prefersReducedMotion() {
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    }

    _runLineClampRaf(outer, inner, fromH, toH, fromLines, toLines) {
        const step = () => {
            if (!this._animating || !this._anim) {
                return;
            }
            const h = outer.getBoundingClientRect().height;
            let p;
            if (toH > fromH) {
                const denom = toH - fromH;
                p = denom <= 0 ? 1 : Math.max(0, Math.min(1, (h - fromH) / denom));
            } else {
                const denom = fromH - toH;
                p = denom <= 0 ? 1 : Math.max(0, Math.min(1, (fromH - h) / denom));
            }
            const lineCount = Math.round(fromLines + (toLines - fromLines) * p);
            const lo = Math.min(fromLines, toLines);
            const hi = Math.max(fromLines, toLines);
            this._applyLineClamp(inner, Math.max(lo, Math.min(hi, lineCount)));

            if (this._anim.playState === 'running') {
                this._lineClampRafId = requestAnimationFrame(step);
            }
        };
        this._lineClampRafId = requestAnimationFrame(step);
    }

    async _toggle() {
        const outer = this._outerRef.value;
        const inner = this._innerRef.value;
        if (!outer || !inner || !this._needsToggle || !this._collapsedHeightPx || !this._lineHeightPx) {
            return;
        }

        this._cancelAnimation();

        if (this._prefersReducedMotion()) {
            this._expanded = !this._expanded;
            this._syncFromState();
            return;
        }

        const targetLines = this.lines;
        const toCollapsedH = this._collapsedHeightPx;

        if (!this._expanded) {
            this._animating = true;
            outer.style.overflow = 'hidden';
            const startH = inner.offsetHeight;
            outer.style.height = `${startH}px`;

            this._expanded = true;
            await this.updateComplete;

            const endH = inner.scrollHeight;
            const maxLines = this._maxContentLines(inner);

            this._anim = outer.animate([{ height: `${startH}px` }, { height: `${endH}px` }], {
                duration: ANIMATION.DURATION_MS,
                easing: ANIMATION.EASING,
                fill: ANIMATION.FILL,
            });

            this._applyLineClamp(inner, targetLines);
            this._runLineClampRaf(outer, inner, startH, endH, targetLines, maxLines);

            try {
                await this._anim.finished;
            } catch {
                this._animating = false;
                this._clearLineClamp(inner);
                this._syncFromState();
                return;
            } finally {
                if (this._lineClampRafId) {
                    cancelAnimationFrame(this._lineClampRafId);
                    this._lineClampRafId = 0;
                }
                this._anim = null;
            }

            this._animating = false;
            this._clearLineClamp(inner);
            outer.style.height = '';
            outer.style.overflow = '';
            this._syncFromState();
        } else {
            this._animating = true;
            const fromH = inner.scrollHeight;
            const maxLines = this._maxContentLines(inner);

            this._applyLineClamp(inner, maxLines);
            outer.style.overflow = 'hidden';
            outer.style.height = `${fromH}px`;
            void inner.offsetHeight;

            this._anim = outer.animate([{ height: `${fromH}px` }, { height: `${toCollapsedH}px` }], {
                duration: ANIMATION.DURATION_MS,
                easing: ANIMATION.EASING,
                fill: ANIMATION.FILL,
            });

            this._runLineClampRaf(outer, inner, fromH, toCollapsedH, maxLines, targetLines);

            try {
                await this._anim.finished;
            } catch {
                this._animating = false;
                this._applyLineClamp(inner, targetLines);
                this._syncFromState();
                return;
            } finally {
                if (this._lineClampRafId) {
                    cancelAnimationFrame(this._lineClampRafId);
                    this._lineClampRafId = 0;
                }
                this._anim = null;
            }

            this._expanded = false;
            this._animating = false;
            this._applyLineClamp(inner, targetLines);
            outer.style.height = '';
            outer.style.overflow = '';
            this._syncFromState();
        }
    }

    render() {
        const label = this._expanded ? getMessage(messages, 'collapse') : getMessage(messages, 'expand');

        return html`
            <div class="outer" ${ref(this._outerRef)}>
                <div class=${classMap({ inner: true })} ${ref(this._innerRef)}>
                    <slot></slot>
                </div>
            </div>
            <sotbit-button
                class="toggle"
                ?hidden=${!this._needsToggle}
                @click=${this._toggle}
                aria-expanded=${this._expanded ? 'true' : 'false'}
                variant="primary"
                size="l"
                inline
            >
                <span slot="content">${label}</span>
                <sotbit-icon
                    slot="icon-right"
                    icon="${this._expanded ? 'chevron-up' : 'chevron-down'}"
                    no-padding
                ></sotbit-icon>
            </sotbit-button>
        `;
    }
}
